import React, { Dispatch, memo, SetStateAction, useCallback, useState } from 'react';

import { Button, Checkbox } from '@components/common';

import { IMavenirConfigToCreate } from '@interfaces/cmModule/dataSources';

import styles from './CheckConfigModal.module.scss';

interface ICheckConfigModalProps {
	existingFiles: IMavenirConfigToCreate[];
	setConfigsToCreate: Dispatch<SetStateAction<IMavenirConfigToCreate[]>>;
}

const CheckConfigModal = ({
	existingFiles,
	setConfigsToCreate,
}: ICheckConfigModalProps) => {
	const [isAllFiles, setIsAllFiles] = useState<boolean | undefined>();

	const isSingleFile = existingFiles.length === 1;

	const currentFile = existingFiles[0]?.fileName || null;

	const groupOperation = useCallback((isReplace: boolean) => {
		if (isReplace) {
			setConfigsToCreate((prevState) => {
				return prevState.map((file) => {
					return {
						...file,
						isSkip: false,
					};
				});
			});
		} else {
			setConfigsToCreate(prev => prev.filter((file) => !file.isSkip));
		}
	}, [setConfigsToCreate]);

	const onSubmit = useCallback(() => {
		if (isAllFiles) {
			groupOperation(true);

			return;
		}

		setConfigsToCreate((prevState) => {
			return prevState.map((file) => {
				if (file.fileName === currentFile) {
					return {
						...file,
						isSkip: false,
					};
				}
				return file;
			});
		});
	}, [currentFile, isAllFiles, setConfigsToCreate, groupOperation]);

	const onChange = useCallback(() => {
		setIsAllFiles(prev => !prev);
	}, []);

	const onSkip = useCallback(() => {
		if (isAllFiles) {
			groupOperation(false);

			return;
		}

		setConfigsToCreate((prevState) => {
			return prevState.filter((file) => file.fileName !== currentFile);
		});
	}, [currentFile, isAllFiles, setConfigsToCreate, groupOperation]);

	return (
		existingFiles.length
			? (
				<div className={styles.container}>
					<div className={styles.question}>
						Do you want to replace existing file or skip it while uploading?
					</div>
					<div className={styles.description}>The file {currentFile} already exists.</div>
					<div className={styles.actions}>
						{!isSingleFile
							? (
								<Checkbox
									checked={isAllFiles}
									label='Use for all files'
									name='forAllFiles'
									onChange={onChange}
								/>
							)
							: <span></span>
						}
						<div className={styles.buttons}>
							<Button
								large
								onClick={onSkip}
								secondary
								text='Skip file'
							/>
							<Button
								large
								onClick={onSubmit}
								text='Replace file'
							/>
						</div>
					</div>
				</div>
			)
			: null
	);
};

export default memo(CheckConfigModal);
